import { useMemo } from 'react';
import {
  FileTextIcon,
  PlayCircleIcon,
  QuizIcon,
  SchoolIcon,
} from '../icons';
import { Box, Card, Typography } from '../ui';
import { Lesson, LessonType, Module } from '../../apis/api-client';

interface CourseOverviewTabProps {
  /** Modules (sections) for the curriculum-at-a-glance list. */
  modules?: Module[];
  /** Full lesson list, used to order lessons module-by-module and
   *  surface any lessons not bucketed into a module. */
  lessons?: Lesson[];
}

/**
 * CourseOverviewTab
 *
 * The "Course Overview" tab on the learner course-detail page: a flat
 * curriculum-at-a-glance list of every lesson in course order with a
 * type icon and (for video) a duration. Purely presentational —
 * extracted from LearnerCourseDetailLayout.
 */
export default function CourseOverviewTab({ modules, lessons }: CourseOverviewTabProps) {
  // Build a flat curriculum-at-a-glance: every lesson in the order
  // it appears in the course, walked module-by-module. Module names
  // are usually a duplicate of the lesson title in this catalog, so
  // we skip them entirely and just show the lesson list with type
  // icons and (for video) durations. Orphan lessons trail the rest.
  const overviewLessons = useMemo(() => {
    const ms = (modules ?? []).slice().sort(
      (a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0),
    );
    const lessonsByModule = new Map<number, Lesson[]>();
    const orphanLessons: Lesson[] = [];
    for (const l of lessons ?? []) {
      if (l.moduleId != null) {
        const bucket = lessonsByModule.get(l.moduleId) ?? [];
        bucket.push(l);
        lessonsByModule.set(l.moduleId, bucket);
      } else {
        orphanLessons.push(l);
      }
    }
    const sortLessons = (arr: Lesson[]) =>
      arr.slice().sort(
        (a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0),
      );
    const flat: Lesson[] = [];
    for (const m of ms) {
      if (m.id != null) {
        flat.push(...sortLessons(lessonsByModule.get(m.id) ?? []));
      }
    }
    flat.push(...sortLessons(orphanLessons));
    return flat;
  }, [modules, lessons]);

  return (
    <Card sx={{ p: { xs: 2, md: 3 } }}>
      {/* Description moved up to the hero — this tab focuses on the
          curriculum: module list, lesson count, progress, and the
          CTA into the player. */}
      {overviewLessons.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" sx={{ display: 'block', mb: 1 }}>
            What's covered
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {overviewLessons.map((l, idx) => {
              const meta = lessonTypeMeta(l.lessonType);
              const duration =
                l.lessonType === LessonType.Video
                  ? formatVideoDuration(l.videoDuration)
                  : '';
              return (
                <Box
                  key={l.id ?? `lesson-${idx}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.25,
                    borderTop: idx === 0 ? 'none' : '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {/* Sequence number — anchors the row and reinforces
                      progression through the course. */}
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: 'rgba(var(--brand-rgb), 0.18)',
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}
                  </Box>
                  {/* Bare type-coloured icon — no backdrop so the
                      glyph itself carries the colour and reads
                      with more contrast against the cyan number
                      disc to its left. */}
                  <Box
                    sx={{
                      color: meta.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      '& svg': { fontSize: 22 },
                    }}
                  >
                    {meta.icon}
                  </Box>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}
                  >
                    {l.title || 'Untitled lesson'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexShrink: 0 }}
                  >
                    {duration || meta.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Bottom "Curriculum / Start course" card was retired —
          the page-level Enroll → Continue / Review button at the
          top right of the tab strip now drives the same action. */}
    </Card>
  );
}

/**
 * Type-coded badge for the lesson icon disc on the Course Overview
 * tab. Each type gets a colour family that lines up with the wider
 * dashboard palette (cyan = video / hero, purple = bookmarked /
 * reference, green = certified / success, orange = checked / quiz).
 */
function lessonTypeMeta(type: LessonType | undefined): {
  label: string;
  icon: React.ReactNode;
  tint: string;
  color: string;
} {
  switch (type) {
    case LessonType.Video:
      return {
        label: 'Video',
        icon: <PlayCircleIcon />,
        tint: 'rgba(var(--brand-rgb), 0.18)',
        color: 'rgb(var(--brand-rgb))',
      };
    case LessonType.PPT:
      return {
        label: 'Slides',
        icon: <FileTextIcon />,
        tint: 'rgba(245,124,0,0.16)',
        color: '#e65100',
      };
    case LessonType.Word:
      return {
        label: 'Document',
        icon: <FileTextIcon />,
        tint: 'rgba(21,101,192,0.14)',
        color: '#1565c0',
      };
    case LessonType.PDF:
      return {
        label: 'PDF',
        icon: <FileTextIcon />,
        tint: 'rgba(198,40,40,0.14)',
        color: '#c62828',
      };
    case LessonType.Text:
      return {
        label: 'Reading',
        icon: <FileTextIcon />,
        tint: 'rgba(106,27,154,0.14)',
        color: '#6a1b9a',
      };
    case LessonType.Quiz:
      return {
        label: 'Quiz',
        icon: <QuizIcon />,
        tint: 'rgba(46,125,50,0.14)',
        color: '#2e7d32',
      };
    case LessonType.Scorm:
      return {
        label: 'Interactive',
        icon: <SchoolIcon />,
        tint: 'rgba(var(--brand-rgb), 0.18)',
        color: 'rgb(var(--brand-rgb))',
      };
    default:
      return {
        label: 'Lesson',
        icon: <SchoolIcon />,
        tint: 'grey.100',
        color: 'text.secondary',
      };
  }
}

/**
 * Formats a video lesson's duration (seconds) into a compact label —
 * "12 min" for sub-hour videos, "1h 04m" for longer ones. Returns ""
 * when the duration is missing or zero so the row falls back to the
 * type label instead of printing "0 min".
 */
function formatVideoDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return '';
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  }
  // Videos under a minute still read as "1 min" rather than "0 min".
  return `${Math.max(1, minutes)} min`;
}
